import React from 'react';
import PropTypes from 'prop-types';
import { isValid } from '@brand/utils';
import './recommended-articles.scss';

const getImageSrc = (image) => {
  if (!image) {
    return null;
  }
  if (typeof image === 'string') {
    return image;
  }
  return image.src || image.url || image.source_url || null;
};

// Rendered widths: a 112px square below 768px; a third of the 86% column
// (under 29vw) up to 990px; a third of the 680px column (about 211px) from
// 991px. The browser picks from imageSrcset against these.
const IMAGE_SIZES = '(min-width: 991px) 212px, (min-width: 768px) 29vw, 112px';

const RecommendedArticles = ({ data, className }) => {
  // A missing heading falls back to the default; one the editor cleared
  // arrives as "" and renders nothing, as render.php does.
  const { heading = 'Recommended Reading', items = [] } = data;

  // With alt="" the title is each card link's only accessible name, so an
  // item without one (an untitled post) is skipped rather than left unnamed.
  const cards = isValid(items) ? items.filter((item) => item && item.title) : [];

  if (!isValid(cards)) {
    return null;
  }

  return (
    <div className={`recommended-articles ${className || ''}`}>
      {heading && <h2 className="recommended-articles__heading">{heading}</h2>}
      <div className="recommended-articles__grid">
        {cards.map((item, i) => {
          const imageSrc = getImageSrc(item.image);

          // Every part of the card is an img or span directly inside the <a>:
          // withArticleTracking only counts clicks on the link itself or on
          // an img/span/h2/h4 child of it, so a wrapper div or an <h3> title
          // would leave those clicks untracked.
          return (
            // rel is "noopener" without "noreferrer": these are internal
            // PureWow links and analytics needs the referrer preserved.
            <a
              key={i}
              href={item.url}
              className="recommended-articles__card"
              target="_blank"
              rel="noopener"
            >
              {imageSrc ? (
                // Empty alt: the title inside this link already names it,
                // so a described image is read twice.
                <img
                  src={imageSrc}
                  srcSet={item.imageSrcset || undefined}
                  sizes={item.imageSrcset ? IMAGE_SIZES : undefined}
                  alt=""
                  className="recommended-articles__image"
                  loading="lazy"
                />
              ) : (
                <span className="recommended-articles__image-placeholder" />
              )}
              {item.category && (
                <span className="recommended-articles__category">
                  {item.category}
                </span>
              )}
              <span className="recommended-articles__title">{item.title}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
};

const imageShape = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.shape({
    src: PropTypes.string,
    url: PropTypes.string,
    source_url: PropTypes.string,
  }),
]);

RecommendedArticles.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
        image: imageShape,
        imageSrcset: PropTypes.string,
        category: PropTypes.string,
      })
    ),
  }),
  className: PropTypes.string,
};

export default RecommendedArticles;
